import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Rocket, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "../components/PasswordInput";

import { _t } from "@/lib/i18n";
export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [sp] = useSearchParams();
  const rawNext = sp.get("next") || "";
  const next = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";
  const [email, setEmail] = useState(sp.get("email") || "");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password, needTotp ? totpCode : undefined);
      toast.success(_t("Bentornato"));
      nav(next);
    } catch (err) {
      const msg = formatApiError(err);
      if (msg === "TOTP_REQUIRED") {
        setNeedTotp(true);
        setError("");
      } else {
        setError(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden cover-orbit">
        <div className="relative z-10 h-full flex flex-col justify-between p-16">
          <Link to="/" className="flex items-center gap-3">
            <Rocket className="w-6 h-6 text-[#FF5722] -rotate-45" />
            <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
          </Link>
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF5722] mb-4">{_t("Il futuro è già qui")}</div>
            <h1 className="font-display text-6xl text-white leading-tight mb-6">{_t("Bentornato in orbita.")}</h1>
            <p className="text-white/60 max-w-md leading-relaxed">{_t("Accedi per continuare a viaggiare nel tempo insieme ai tuoi ricordi.")}</p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{_t("MMXXVI · Custodi del tempo")}</div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FF5722] mb-3">{_t("Accedi")}</div>
            <h2 className="font-display text-4xl text-white mb-2">{_t("Il tuo hangar ti aspetta")}</h2>
          </div>

          {!needTotp && (
            <div className="space-y-5 mb-6">
              <div><label className="tc-label">{_t("Email")}</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="tc-input" placeholder="tu@esempio.com" autoComplete="email" /></div>
              <div><label className="tc-label">{_t("Password")}</label>
                <PasswordInput testId="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" autoComplete="current-password" /></div>
            </div>
          )}

          {needTotp && (
            <div className="mb-6">
              <div className="flex items-center gap-2 text-[#FF5722] mb-3">
                <ShieldCheck className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-[0.3em]">{_t("Verifica in due passaggi")}</span>
              </div>
              <p className="text-sm text-white/70 mb-4">{_t("Inserisci il codice a 6 cifre generato dalla tua app di autenticazione.")}</p>
              <label className="tc-label">{_t("Codice 2FA")}</label>
              <input
                data-testid="login-totp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                required
                className="tc-input font-mono-launch text-2xl tracking-[0.4em] text-center"
                placeholder="000000"
                autoFocus
              />
              <button
                type="button"
                onClick={() => { setNeedTotp(false); setTotpCode(""); setError(""); }}
                className="mt-3 text-xs text-white/50 hover:text-white"
              >{_t("← Torna a email/password")}</button>
            </div>
          )}

          {error && <div data-testid="login-error" className="mb-6 text-sm text-red-300 border border-red-500/30 bg-red-500/10 rounded-md p-3">{error}</div>}

          <button data-testid="login-submit" type="submit" disabled={loading} className="btn-launch w-full">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />{" "}{_t("Accesso...")}</> : <><Rocket className="w-4 h-4 -rotate-45" /> {needTotp ? _t("Verifica") : _t("Entra")}</>}
          </button>

          <p className="text-center mt-8 text-sm text-white/60">{_t("Nuovo qui?")}{" "}<Link to="/register" data-testid="link-register" className="text-[#FF5722] hover:underline underline-offset-4">{_t("Crea un account")}</Link>
            <span className="mx-2 text-white/20">·</span>
            <Link to="/forgot-password" data-testid="link-forgot" className="text-white/60 hover:text-white hover:underline underline-offset-4">{_t("Password dimenticata?")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
